import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState, useMemo } from 'react';
import AnalyticsIcon from '@heroicons/react/outline/ChartBarIcon';
import DocumentTextIcon from '@heroicons/react/outline/DocumentTextIcon';
import ExclamationIcon from '@heroicons/react/outline/ExclamationIcon';
import EyeOffIcon from '@heroicons/react/outline/EyeOffIcon';
import FolderDownloadIcon from '@heroicons/react/outline/FolderDownloadIcon';
import LocationMarkerIcon from '@heroicons/react/outline/LocationMarkerIcon';
import PencilAltIcon from '@heroicons/react/outline/PencilAltIcon';
import ShieldExclamationIcon from '@heroicons/react/outline/ShieldExclamationIcon';
import TerminalIcon from '@heroicons/react/outline/TerminalIcon';
import TrashIcon from '@heroicons/react/outline/TrashIcon';
import { nanoid } from 'nanoid';
import { hasActionPermission } from '@tribeplatform/gql-client';
import { hasScopesPermission } from '@tribeplatform/gql-client/lib';
import { PinnedInto, ModerationEntityType, PermissionContext, } from '@tribeplatform/gql-client/types';
import { useRouter, useTribeClient } from '@tribeplatform/react-sdk';
import { useAuthMember, useDeletePost, useHidePost, useNetwork, usePinPostToSpace, usePost, useUnhidePost, useUnpinPostFromSpace, } from '@tribeplatform/react-sdk/hooks';
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar';
import { confirm } from '@tribeplatform/react-ui-kit/Dialog';
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons';
import { toast } from '@tribeplatform/react-ui-kit/Toast';
import { PostAnalyticsModal } from '../../Analytics/PostAnalytics';
import { AuditLogsModal } from '../../AuditLog/AuditLogsModal';
import { IconEmoji } from '../../common';
import { useI18n } from '../../i18n';
import { ReportModal } from '../../ReportModal';
import { MovePostModal } from '../MovePostModal';
export const usePostActionItems = ({ post, context, fetchShortcuts, }) => {
    var _a, _b, _c, _d, _e, _f;
    const { $t } = useI18n();
    const actionItems = [];
    const router = useRouter();
    const { config } = useTribeClient();
    const [shortcutActions, setShortcutActions] = useState();
    const [loadingShortcuts, setLoadingShortcuts] = useState({});
    const { interactionsContext: { useInteractions }, } = config !== null && config !== void 0 ? config : {};
    const { loadInteractions } = useInteractions({});
    const [auditLogsModal, setAuditLogsModal] = useState(false);
    const [postAnalyticsModal, setPostAnalyticsModal] = useState(false);
    const [movePostModal, setMovePostModal] = useState(false);
    const [reportModal, setReportModal] = useState(false);
    useEffect(() => {
        setAuditLogsModal(false);
        setPostAnalyticsModal(false);
        setMovePostModal(false);
        setReportModal(false);
    }, [router.params]);
    const { mutate: deletePost } = useDeletePost();
    const { mutate: hidePost } = useHidePost();
    const { mutate: unhidePost } = useUnhidePost();
    const { mutate: pinPostToSpace } = usePinPostToSpace();
    const { mutate: unpinPostFromSpace } = useUnpinPostFromSpace();
    const { data: network } = useNetwork();
    const { isAdmin } = useAuthMember();
    const categoryText = $t({
        id: 'Generics.Post.Noun',
        defaultMessage: 'Post',
    });
    const { data, isStale } = usePost({
        variables: { id: post === null || post === void 0 ? void 0 : post.id },
        fields: { shortcuts: { favicon: 'all' } },
        useQueryOptions: {
            enabled: !!post && fetchShortcuts,
        },
    });
    const shortcuts = useMemo(() => { var _a; return (_a = data === null || data === void 0 ? void 0 : data.shortcuts) !== null && _a !== void 0 ? _a : []; }, [data === null || data === void 0 ? void 0 : data.shortcuts]);
    const callLoadAppInteractions = useCallback(async ({ onSettle, shortcutKey, appId }) => {
        var _a, _b, _c;
        setLoadingShortcuts(loadingShortcuts => ({
            ...loadingShortcuts,
            [shortcutKey]: true,
        }));
        try {
            await loadInteractions({
                shortcutKey,
                interactionId: nanoid(),
                appId,
                context: PermissionContext.POST,
                entityId: post ? post.id : '',
            });
        }
        catch (e) {
            toast({
                title: $t({
                    defaultMessage: 'Something went wrong',
                    id: 'Generics.SomethingWentWrong',
                }),
                description: (_c = (_b = (_a = e) === null || _a === void 0 ? void 0 : _a.response) === null || _b === void 0 ? void 0 : _b.errors[0]) === null || _c === void 0 ? void 0 : _c.message,
                status: 'error',
            });
        }
        setLoadingShortcuts(loadingShortcuts => ({
            ...loadingShortcuts,
            [shortcutKey]: false,
        }));
        if (onSettle) {
            onSettle();
        }
    }, [$t, loadInteractions, post]);
    const getIcon = useCallback((shortcut) => {
        var _a, _b;
        const iconUrl = (_a = shortcut.favicon) === null || _a === void 0 ? void 0 : _a.url;
        const iconText = (_b = shortcut.favicon) === null || _b === void 0 ? void 0 : _b.text;
        if (loadingShortcuts[shortcut.key]) {
            return _jsx(SpinnerIcon, { className: "animate-spin h-5 w-5" }, void 0);
        }
        if (iconUrl) {
            return (_jsx(Avatar, { size: "xxs", rounded: "sm", name: shortcut.name, src: iconUrl, className: "flex" }, void 0));
        }
        if (iconText) {
            return _jsx(IconEmoji, { text: iconText }, void 0);
        }
        return TerminalIcon;
    }, [loadingShortcuts]);
    useEffect(() => {
        if (shortcuts && !isStale) {
            const actions_ = shortcuts.map(shortcut => {
                const shortcutAction = {
                    action: options => callLoadAppInteractions({
                        onSettle: options === null || options === void 0 ? void 0 : options.onSettle,
                        shortcutKey: shortcut.key,
                        appId: shortcut.appId,
                    }),
                    icon: getIcon(shortcut),
                    name: shortcut.name,
                    category: categoryText,
                    disabled: loadingShortcuts[shortcut.key],
                };
                return shortcutAction;
            });
            setShortcutActions([...actions_]);
        }
    }, [
        shortcuts,
        isStale,
        loadingShortcuts,
        callLoadAppInteractions,
        getIcon,
        categoryText,
    ]);
    if (!(post === null || post === void 0 ? void 0 : post.id)) {
        return { actions: [], shortcuts: [], children: null };
    }
    const [canUpdatePost, canRemovePost, canHidePost] = hasScopesPermission(post, ['updatePost', 'removePost', 'hidePost']);
    const canViewAnalytics = ((_c = hasActionPermission((_b = (_a = network === null || network === void 0 ? void 0 : network.authMemberProps) === null || _a === void 0 ? void 0 : _a.permissions) !== null && _b !== void 0 ? _b : [], 'analytics')) === null || _c === void 0 ? void 0 : _c.authorized) && isAdmin;
    const [canUpdateNetwork, canReportPost] = hasScopesPermission(network, [
        'updateNetwork',
        'createModerationReport',
    ]);
    const [canMovePost] = hasScopesPermission(network, ['movePost']);
    const [canPinPost, canUnpinPost] = hasScopesPermission(post === null || post === void 0 ? void 0 : post.space, [
        'pinPostToSpace',
        'unpinPostFromSpace',
    ]);
    const isPinned = (_d = post === null || post === void 0 ? void 0 : post.pinnedInto) === null || _d === void 0 ? void 0 : _d.includes(PinnedInto.space);
    const canPin = canPinPost && ((_e = context === 'post') !== null && _e !== void 0 ? _e : context === 'space');
    const canUnpin = canUnpinPost && ((_f = context === 'post') !== null && _f !== void 0 ? _f : context === 'space');
    const postPath = post === null || post === void 0 ? void 0 : post.relativeUrl;
    const isPostPage = router.location.pathname === postPath;
    const canViewPost = context === 'admin';
    const removePost = async () => {
        var _a;
        if (!(post === null || post === void 0 ? void 0 : post.id)) {
            return;
        }
        const confirmed = await confirm({
            title: $t({
                id: 'Post.RemovePost.Confirmation',
                defaultMessage: 'Are you sure you want to remove this post?',
            }),
            proceedLabel: $t({
                id: 'Generics.Remove',
                defaultMessage: 'Remove',
            }),
            cancelLabel: $t({
                id: 'Generics.Cancel',
                defaultMessage: 'Cancel',
            }),
            danger: true,
        });
        if (!confirmed) {
            return;
        }
        try {
            deletePost({ id: post === null || post === void 0 ? void 0 : post.id });
            toast({
                title: $t({
                    id: 'Post.Removed',
                    defaultMessage: 'Post removed',
                }),
                description: $t({
                    id: 'Post.Removed.Description',
                    defaultMessage: 'Post is successfully removed.',
                }),
                status: 'success',
            });
            if (isPostPage) {
                const space = post === null || post === void 0 ? void 0 : post.space;
                router.replace((_a = space === null || space === void 0 ? void 0 : space.relativeUrl) !== null && _a !== void 0 ? _a : '/');
            }
        }
        catch (err) {
            toast({
                title: $t({
                    id: 'Generics.SomethingWentWrong',
                    defaultMessage: 'Something went wrong',
                }),
                description: $t({
                    id: 'Post.UnableToRemove',
                    defaultMessage: 'Cannot remove this post.',
                }),
                status: 'error',
            });
        }
    };
    const toggleHidePost = () => {
        if (post.isHidden) {
            unhidePost({
                id: post.id,
            });
        }
        else {
            hidePost({
                id: post.id,
            });
        }
    };
    if (canViewPost) {
        actionItems.push({
            url: postPath,
            icon: DocumentTextIcon,
            name: $t({
                id: 'Post.View',
                defaultMessage: 'View post',
            }),
            category: categoryText,
        });
    }
    if (canPin && isPinned) {
        actionItems.push({
            action: () => unpinPostFromSpace({ postId: post.id }),
            icon: LocationMarkerIcon,
            name: $t({
                id: 'Post.Unpin',
                defaultMessage: 'Unpin post',
            }),
            category: categoryText,
            hotkey: 'p',
        });
    }
    if (canUnpin && !isPinned) {
        actionItems.push({
            action: () => pinPostToSpace({ postId: post.id }),
            icon: LocationMarkerIcon,
            name: $t({
                id: 'Post.Pin',
                defaultMessage: 'Pin post',
            }),
            category: categoryText,
            hotkey: 'p',
        });
    }
    if (canUpdatePost) {
        actionItems.push({
            url: `${post === null || post === void 0 ? void 0 : post.relativeUrl}/edit`,
            icon: PencilAltIcon,
            name: $t({
                id: 'Post.Edit',
                defaultMessage: 'Edit post',
            }),
            category: categoryText,
            hotkey: 'e',
        });
    }
    if (canMovePost) {
        actionItems.push({
            action: () => setMovePostModal(true),
            icon: FolderDownloadIcon,
            name: $t({
                id: 'Post.Move',
                defaultMessage: 'Move post',
            }),
            category: categoryText,
            hotkey: 'm',
        });
    }
    if (canViewAnalytics) {
        actionItems.push({
            action: () => setPostAnalyticsModal(true),
            icon: AnalyticsIcon,
            name: $t({
                id: 'Post.Analytics',
                defaultMessage: 'Post analytics',
            }),
            hotkey: 'option+a,alt+a',
            category: categoryText,
        });
    }
    if (canUpdateNetwork) {
        actionItems.push({
            action: () => setAuditLogsModal(true),
            icon: ShieldExclamationIcon,
            name: $t({
                id: 'Generics.AuditLogs',
                defaultMessage: 'Audit logs',
            }),
            hotkey: 'option+l,alt+l',
            category: categoryText,
        });
    }
    if (canHidePost) {
        actionItems.push({
            action: () => toggleHidePost(),
            icon: EyeOffIcon,
            name: post.isHidden
                ? $t({
                    id: 'Post.Unhide',
                    defaultMessage: 'Unhide post',
                })
                : $t({
                    id: 'Post.Hide',
                    defaultMessage: 'Hide post',
                }),
            category: categoryText,
            hotkey: 'h',
        });
    }
    if (canRemovePost) {
        actionItems.push({
            action: () => removePost(),
            icon: TrashIcon,
            name: $t({
                id: 'Post.Delete',
                defaultMessage: 'Delete post',
            }),
            category: categoryText,
            hotkey: 'cmd+backspace,ctrl+backspace',
        });
    }
    if (canReportPost) {
        actionItems.push({
            action: () => setReportModal(true),
            icon: ExclamationIcon,
            name: $t({
                id: 'Post.Report',
                defaultMessage: 'Report post',
            }),
            category: categoryText,
        });
    }
    const children = (_jsxs(_Fragment, { children: [auditLogsModal && (_jsx(AuditLogsModal, { postId: post === null || post === void 0 ? void 0 : post.id, onClose: () => setAuditLogsModal(false), open: true }, void 0)), movePostModal && (_jsx(MovePostModal, { post: post, onClose: () => setMovePostModal(false), open: true }, void 0)), postAnalyticsModal && (_jsx(PostAnalyticsModal, { post: post, onClose: () => setPostAnalyticsModal(false), linkToContent: false, open: true }, void 0)), reportModal && (_jsx(ReportModal, { entity: post, entityType: ModerationEntityType.POST, onClose: () => setReportModal(false), open: true }, void 0))] }, void 0));
    return {
        actions: actionItems,
        shortcuts: shortcutActions,
        children,
    };
};
//# sourceMappingURL=usePostActionItems.js.map